import { useEffect, useState } from 'react';
import ScrollWorld from '@/components/ScrollWorld';
import BookModal from '@/components/BookModal';
import Preloader from '@/components/Preloader';
import { DEMO_URL } from '@/lib/analytics';

// The single Vouch landing page: a preload gate holds scroll until every scene asset is
// cached, then the scroll-world flight, the fixed top-right chrome, and the static closing
// "landing strip" that converts on its own if you skip the flight.
export default function Landing() {
  const [ready, setReady] = useState(false);   // all assets cached → flight is scrollable
  const [hidden, setHidden] = useState(false);  // overlay finished fading → unmount it
  const demoHref = DEMO_URL;                     // same-origin demo — no UTM tracking on the link

  // Lock scroll until the flight is ready (and never leave the lock behind on unmount).
  useEffect(() => {
    document.body.classList.toggle('v-noscroll', !ready);
    return () => document.body.classList.remove('v-noscroll');
  }, [ready]);

  // Once ready, let the overlay's CSS fade run, then unmount it. The timeout matches the
  // .v-preload opacity transition and also covers reduced-motion (where no transition fires).
  useEffect(() => {
    if (!ready) return;
    const t = window.setTimeout(() => setHidden(true), 700);
    return () => window.clearTimeout(t);
  }, [ready]);

  return (
    <>
      <a id="top" />

      {/* scroll-world flight — mounts only once assets are cached, so the engine's lazy
          clip fetches are all instant cache hits */}
      {ready && <ScrollWorld />}

      {/* top-right chrome (skip + gated demo ghost) */}
      <div className="v-topright">
        <a className="v-skip" href="#closing" data-skip>Skip the flight ↓</a>
        <a className="v-demo-ghost" data-demo href={demoHref}>Enter the demo</a>
      </div>

      {/* static closing section */}
      <section id="closing" className="v-closing">
        <div className="v-closing__inner">
          <span className="v-closing__eyebrow">VOUCH · TECH LOG &amp; CAMO</span>
          <h2>Records that vouch for themselves.</h2>
          <p>
            Vouch is a records-centric aviation maintenance platform — electronic tech log, defect
            and MEL deferral management, CAMO document and requirement tracking, work orders, and CRS
            release. One aircraft, one continuous record, from touchdown to release. This page is the
            front door; the demo is the real thing — three minutes, real screens.
          </p>
          <div className="v-closing__cta">
            <a className="v-btn v-btn--primary" data-demo href={demoHref}>Enter the demo</a>
            <a className="v-btn v-btn--ghost" data-book href="#book">Book a walkthrough</a>
          </div>
          <footer className="v-foot">A demonstration environment. Data is simulated.</footer>
        </div>
      </section>

      {/* lead modal */}
      <BookModal />

      {/* preload gate on top of everything until assets are cached */}
      {!hidden && <Preloader done={ready} onReady={() => setReady(true)} />}
    </>
  );
}
